import { useStation } from '@app/core/stations/hooks/useStation';
import { useStreamerBoxDeviceForStation } from '@app/modules/logger/screens/status/hooks/useStreamerBoxDeviceForStation';
import { StreamerBoxIcon } from '@app/modules/streamer-box/components/StreamerBoxIcon';
import { theme } from '@mi-front/ui';
import { Chip, IconButton, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

interface Props {
  stationId: string;
  compact?: boolean;
}

/**
 * Self-contained component that displays the StreamerBox status for a station.
 * It fetches the station data and device status internally.
 * In compact mode, shows an icon button instead of a chip.
 */
export function StreamerBoxStatus(p: Props) {
  const { stationId, compact = false } = p;
  const station = useStation(stationId);
  const { device } = useStreamerBoxDeviceForStation(station);

  const deviceId = station.featuresConfig?.streamerBox?.deviceId;

  // Only render if the station has a deviceId configured
  if (!deviceId) {
    return null;
  }

  const iconColor =
    device?.isOnline === true
      ? theme.green
      : device?.isOnline === false
        ? theme.red
        : theme.gray500;

  if (compact) {
    return (
      <Tooltip
        title={
          <FormattedMessage
            id="logger.streamerBox.goToDevice"
            defaultMessage="Ir al dispositivo Streamer Box"
          />
        }
      >
        <IconButton
          component={Link}
          to={`/streamer-box/devices/${deviceId}`}
          size="small"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
          sx={{ mr: 0.5 }}
        >
          <StreamerBoxIcon
            fontSize="small"
            style={{
              textDecoration: 'none',
              color: iconColor,
            }}
          />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Chip
      size="small"
      variant="outlined"
      sx={{ mr: 1.5, pl: 0.5, cursor: 'pointer', color: 'text.secondary' }}
      icon={
        <StreamerBoxIcon
          fontSize="small"
          style={{
            textDecoration: 'none',
            color: iconColor,
          }}
        />
      }
      to={`/streamer-box/devices/${deviceId}`}
      component={Link}
      label={deviceId}
      onClick={(e) => e.stopPropagation()}
    />
  );
}
